import { LOCALE_STORAGE_KEYS } from 'common/constants/localeStorageConstants'
import { UtilService } from 'common/services/utilService'

import type { ScheduleZoomInterval } from '../Calendar/ZoomControls/scheduleZoomIntervals'
import type { ScheduleRoomsPreference } from './scheduleRoomsOrder'

export type ScheduleColumnWidthView = 'day' | 'week' | 'day-staff'
export type ScheduleCalendarView = 'day' | 'week'

type UserColumnWidths = Partial<Record<ScheduleColumnWidthView, Record<string, number>>>

type UserSchedulePreferences = {
  columnWidths?: UserColumnWidths
  doctorRailOrder?: number[]
  doctorRailSelection?: number[]
  rooms?: ScheduleRoomsPreference
  view?: ScheduleCalendarView
  zoom?: ScheduleZoomInterval
}

type SchedulePreferencesStore = Record<string, UserSchedulePreferences>

const LEGACY_COLUMN_WIDTHS_KEY = 'scheduleColumnWidths'
const LEGACY_DOCTOR_RAIL_ORDER_KEY = 'scheduleDoctorRailOrder'

export const isSchedulePreferenceUserId = (
  userId: number | null | undefined,
): userId is number => userId != null && !Number.isNaN(Number(userId))

const readJsonObject = (raw: string | null): Record<string, unknown> | null => {
  if (!raw) return null
  const parsed = UtilService.safeParseJSON<unknown>(raw, null)
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null
  return parsed as Record<string, unknown>
}

const readStore = (): SchedulePreferencesStore => {
  try {
    const current =
      (readJsonObject(window.localStorage.getItem(LOCALE_STORAGE_KEYS.SCHEDULE_PREFERENCES)) as
        | SchedulePreferencesStore
        | null) ?? {}
    const legacyWidths = readJsonObject(window.localStorage.getItem(LEGACY_COLUMN_WIDTHS_KEY))
    const legacyDoctors = readJsonObject(window.localStorage.getItem(LEGACY_DOCTOR_RAIL_ORDER_KEY))
    if (!legacyWidths && !legacyDoctors) return current

    const merged = mergeLegacyPreferences(current, legacyWidths, legacyDoctors)
    writeStore(merged)
    window.localStorage.removeItem(LEGACY_COLUMN_WIDTHS_KEY)
    window.localStorage.removeItem(LEGACY_DOCTOR_RAIL_ORDER_KEY)
    return merged
  } catch {
    return {}
  }
}

const mergeLegacyPreferences = (
  current: SchedulePreferencesStore,
  legacyWidths: Record<string, unknown> | null,
  legacyDoctors: Record<string, unknown> | null,
): SchedulePreferencesStore => {
  const next: SchedulePreferencesStore = { ...current }
  const userIds = new Set([
    ...Object.keys(current),
    ...Object.keys(legacyWidths ?? {}),
    ...Object.keys(legacyDoctors ?? {}),
  ])

  for (const userId of userIds) {
    const entry: UserSchedulePreferences = { ...(next[userId] ?? {}) }
    const widths = legacyWidths?.[userId]
    const doctors = legacyDoctors?.[userId]
    if (!entry.columnWidths && widths && typeof widths === 'object' && !Array.isArray(widths)) {
      entry.columnWidths = widths as UserColumnWidths
    }
    if (!entry.doctorRailOrder && Array.isArray(doctors)) {
      entry.doctorRailOrder = doctors as number[]
    }
    next[userId] = entry
  }

  return next
}

const writeStore = (store: SchedulePreferencesStore): void => {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEYS.SCHEDULE_PREFERENCES, JSON.stringify(store))
  } catch {
    // Storage can fail (private mode, quota); ignore.
  }
}

export const readSchedulePreferences = (): SchedulePreferencesStore => readStore()

export const updateSchedulePreferences = (
  userId: number | null | undefined,
  updater: (current: UserSchedulePreferences) => UserSchedulePreferences,
): UserSchedulePreferences | null => {
  if (!isSchedulePreferenceUserId(userId)) return null
  const store = readStore()
  const key = String(userId)
  const next = updater({ ...(store[key] ?? {}) })
  store[key] = next
  writeStore(store)
  return next
}

const cleanRoomIds = (value: unknown): string[] => {
  if (!Array.isArray(value)) return []
  const ids: string[] = []
  const seen = new Set<string>()
  for (const entry of value) {
    const id = String(entry ?? '').trim()
    if (!id || seen.has(id)) continue
    seen.add(id)
    ids.push(id)
  }
  return ids
}

export const getScheduleRoomsPreference = (
  userId: number | null | undefined,
): ScheduleRoomsPreference => {
  if (!isSchedulePreferenceUserId(userId)) return { order: [], hidden: [] }
  const rooms = readStore()[String(userId)]?.rooms
  return {
    order: cleanRoomIds(rooms?.order),
    hidden: cleanRoomIds(rooms?.hidden),
  }
}

export const setScheduleRoomsPreference = (
  userId: number | null | undefined,
  preference: ScheduleRoomsPreference,
): ScheduleRoomsPreference => {
  const next = {
    order: cleanRoomIds(preference.order),
    hidden: cleanRoomIds(preference.hidden),
  }
  if (!isSchedulePreferenceUserId(userId)) return next
  updateSchedulePreferences(userId, (current) => ({ ...current, rooms: next }))
  return next
}
